import {
  compact,
  isArray,
  isBoolean,
  isNumber,
  isNumeric,
  isObject,
  mapValues,
  memoize,
  parseURLQuery,
  stringifyURLQuery,
} from '@seiue/util'
import { matchPath } from 'react-router-dom'

import { MODAL_QUERY_NAMESPACE, RouteNode, RouteParams } from './types'

/**
 * 对 routes 进行排序，当两个 route.path 之间是包含关系时，让更具体的 route 在先，更泛的在后
 * @param _routes 原 routes
 * @return 排序后 routes
 */
export const sortRoutes = (_routes: any[]) => {
  const pattern = new RegExp(':[^/]+', 'g')
  _routes.sort((current, prev) => {
    const modifiedCurrentPath = current.path.replace(pattern, ':')
    const modifiedPrevPath = prev.path.replace(pattern, ':')

    if (modifiedCurrentPath.startsWith(modifiedPrevPath)) return -1
    if (modifiedPrevPath.startsWith(modifiedCurrentPath)) return 1

    // 毫无关系的 route 之间保持原序
    return 0
  })

  return _routes
}

export const clearModalQuery = (query: string | object) => {
  const nextQuery: { [key: string]: any; modal?: string } = isObject(query)
    ? { ...query }
    : parseURLQuery(query)

  delete nextQuery.modal
  delete nextQuery[MODAL_QUERY_NAMESPACE]

  return nextQuery
}

/**
 * breaks /a/b/c into /, /a, /a/b, /a/b/c
 */
export const getPathAncestors = (path: string) => {
  const segments = compact(path.split('/'))
  const ancestors: string[] = ['/']
  segments.forEach((seg, idx) => {
    if (idx === 0) ancestors.push(`/${seg}`)
    else ancestors.push(`${ancestors[idx]}/${seg}`)
  })

  return ancestors
}

export const findMatchRoute = memoize((path: string, routes: RouteNode[]) => {
  const matchRoute = (route: RouteNode): RouteNode | null => {
    const matched = matchPath(path, {
      path: route.path,
      exact: true,
      strict: false,
    })

    if (matched) return route

    if (route.subRoutes) {
      for (const sr of route.subRoutes) {
        // Don't return sr. Return the route returned from matchRoute(sr).
        const result = matchRoute(sr)
        if (result) return result
      }
    }

    return null
  }

  for (const route of routes) {
    const result = matchRoute(route)
    if (result) return result
  }

  return null
})

// 允许 route params 使用 string 外的各种类型, 在被 route 使用前转为 string
export const formatParams = (params: RouteParams) =>
  mapValues(params, (val) => {
    if (isArray(val)) return val.join(',')
    if (isNumber(val) || isBoolean(val)) return `${val}`

    return val
  })

/**
 * 添加新的 query 内容到 query string 当中
 *
 * @param prevQueryString - 旧的 queryString
 * @param toAppendQuery - 新的 query 内容
 * @returns query 字符串
 */
export function appendQueryString(
  prevQueryString: string,
  toAppendQuery: object
): string {
  const newQuery = {
    ...parseURLQuery(prevQueryString),
    ...toAppendQuery,
  }

  return stringifyURLQuery(newQuery)
}

// 允许 route params 使用 string 外的各种类型, 在开发者使用前由 string 转为原类型
export const parseParams = (params: { [key: string]: string }): RouteParams =>
  mapValues(params, val => {
    /**
     * 已知问题：传递的参数在浏览器刷新后会被做一次 encode，
     * 每次刷新页面都有一次浏览器的 encode ，还不确定问题原因
     */
    if (val === 'true') return true
    if (val === 'false') return false
    if (isNumeric(val)) return +val

    // val 可能为 undefined
    if (val?.includes && val.includes(',')) {
      const array = val.split(',')
      if (array.every(isNumeric)) return array.map(Number)
      return array
    }

    return val
  })
